import React from 'react';
import { Rss, Globe, Hash } from 'lucide-react';

interface SourceBadgeProps {
  source_feed: string;
  username?: string;
  size?: 'sm' | 'md' | 'lg';
  showIcon?: boolean;
}

export const SourceBadge: React.FC<SourceBadgeProps> = ({ 
  source_feed, 
  username,
  size = 'md',
  showIcon = true 
}) => {
  // Determine source type and display info
  const getSourceInfo = (source: string, username?: string) => {
    if (source.startsWith('rss-app-')) {
      return {
        type: 'RSS.app',
        displayName: username || source.replace('rss-app-', '').replace(/-/g, ' '),
        icon: Hash,
        bgColor: 'bg-purple-100',
        textColor: 'text-purple-800',
        borderColor: 'border-purple-200'
      };
    } else if (source.includes('.')) {
      // Likely a domain name
      return {
        type: 'RSS Feed',
        displayName: source,
        icon: Rss,
        bgColor: 'bg-orange-100',
        textColor: 'text-orange-800',
        borderColor: 'border-orange-200'
      };
    } else {
      // Other source type
      return {
        type: 'Source',
        displayName: source,
        icon: Globe,
        bgColor: 'bg-gray-100',
        textColor: 'text-gray-800',
        borderColor: 'border-gray-200'
      };
    }
  };

  const sourceInfo = getSourceInfo(source_feed, username);
  const Icon = sourceInfo.icon;

  // Size configurations
  const sizeConfig = {
    sm: {
      container: 'px-2 py-1 text-xs',
      icon: 'w-3 h-3',
      gap: 'gap-1'
    },
    md: {
      container: 'px-3 py-1.5 text-sm',
      icon: 'w-4 h-4',
      gap: 'gap-2'
    },
    lg: {
      container: 'px-4 py-2 text-base',
      icon: 'w-5 h-5',
      gap: 'gap-2'
    }
  };

  const config = sizeConfig[size];

  return (
    <div 
      className={`
        inline-flex items-center ${config.gap} 
        ${config.container} 
        ${sourceInfo.bgColor} 
        ${sourceInfo.textColor} 
        ${sourceInfo.borderColor}
        border rounded-full font-medium
        transition-colors hover:opacity-80
      `}
      title={`Source: ${source_feed} (${sourceInfo.type})`}
    >
      {showIcon && <Icon className={config.icon} />}
      <span className="font-medium">{sourceInfo.type}</span>
      <span className="text-xs opacity-75">•</span>
      <span className="truncate max-w-32">{sourceInfo.displayName}</span>
    </div>
  );
};